import React, { useState } from 'react';
import { trackByWaybill } from '../services/orderService';
import toast from 'react-hot-toast';
import { showErrorToast } from '../utils/errorHandler';

function PublicTracking() {
  const [waybill, setWaybill] = useState('');
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!waybill.trim()) {
      toast.error('Please enter tracking ID');
      return;
    }

    setLoading(true);
    setError(null);
    setTrackingData(null);

    try {
      const data = await trackByWaybill(waybill.trim());
      setTrackingData(data);
      toast.success('Tracking information loaded');
    } catch (err) {
      setError(err.message || 'Failed to fetch tracking information');
      showErrorToast(err, 'Failed to fetch tracking information');
    } finally {
      setLoading(false);
    }
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'ordered':
        return '📋';
      case 'being_made':
        return '🏭';
      case 'shipped':
        return '🚚';
      case 'delivered':
        return '✅';
      default:
        return '📦';
    }
  };

  const getStageLabel = (stage) => {
    switch (stage) {
      case 'ordered':
        return 'Order Placed';
      case 'being_made':
        return 'Being Prepared';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      default:
        return stage;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const categorizeScan = (scanDetail) => {
    if (!scanDetail) return 'Unknown';
    const detail = scanDetail.toLowerCase();
    
    if (detail.includes('delivered')) return 'Delivered';
    if (detail.includes('out for delivery') || detail.includes('loaded for delivery')) return 'Out for Delivery';
    if (detail.includes('in transit') || detail.includes('dispatched')) return 'In Transit';
    if (detail.includes('picked up') || detail.includes('pickup')) return 'Picked Up';
    if (detail.includes('manifested') || detail.includes('booked')) return 'Order Booked';
    if (detail.includes('pending') || detail.includes('exception') || detail.includes('undelivered')) return 'Exception';
    
    return 'In Transit';
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Out for Delivery':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In Transit':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Picked Up':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Order Booked':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Exception':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDeliveryProgress = (order) => {
    if (!order?.delhivery?.scans || order.delhivery.scans.length === 0) {
      return {
        stages: [
          { name: 'Order Placed', status: 'completed', icon: '📋' },
          { name: 'Picked Up', status: 'pending', icon: '📦' },
          { name: 'In Transit', status: 'pending', icon: '🚛' },
          { name: 'Out for Delivery', status: 'pending', icon: '🚚' },
          { name: 'Delivered', status: 'pending', icon: '✅' },
        ],
        currentStage: 'Order Placed'
      };
    }

    const scans = order.delhivery.scans;
    const stages = [
      { name: 'Order Placed', status: 'completed', icon: '📋' },
      { name: 'Picked Up', status: 'pending', icon: '📦' },
      { name: 'In Transit', status: 'pending', icon: '🚛' },
      { name: 'Out for Delivery', status: 'pending', icon: '🚚' },
      { name: 'Delivered', status: 'pending', icon: '✅' },
    ];

    let currentStage = 'Order Placed';

    scans.forEach(scan => {
      const detail = scan.ScanDetail?.toLowerCase() || '';
      
      if (detail.includes('delivered')) {
        stages[4].status = 'completed';
        stages[3].status = 'completed';
        stages[2].status = 'completed';
        stages[1].status = 'completed';
        currentStage = 'Delivered';
      } else if (detail.includes('out for delivery') || detail.includes('loaded for delivery')) {
        stages[3].status = 'completed';
        stages[2].status = 'completed';
        stages[1].status = 'completed';
        if (currentStage !== 'Delivered') currentStage = 'Out for Delivery';
      } else if (detail.includes('in transit') || detail.includes('dispatched')) {
        stages[2].status = 'completed';
        stages[1].status = 'completed';
        if (currentStage !== 'Out for Delivery' && currentStage !== 'Delivered') {
          currentStage = 'In Transit';
        }
      } else if (detail.includes('picked up') || detail.includes('pickup')) {
        stages[1].status = 'completed';
        if (currentStage === 'Order Placed') currentStage = 'Picked Up';
      }
    });

    // Set current active stage
    const lastCompletedIndex = stages.findIndex((s, i) => i > 0 && s.status === 'completed' && stages[i + 1]?.status === 'pending');
    if (lastCompletedIndex !== -1 && lastCompletedIndex < stages.length - 1) {
      stages[lastCompletedIndex + 1].status = 'current';
    }

    return { stages, currentStage };
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Track Your Order</h1>
          <p className="text-gray-600">Enter your tracking ID to see the latest shipment status</p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleTrack} className="flex gap-4">
            <input
              type="text"
              value={waybill}
              onChange={(e) => setWaybill(e.target.value)}
              placeholder="Enter Tracking ID / Waybill Number"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? 'Tracking...' : 'Track'}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-2 text-red-800">
              <span className="text-xl">⚠️</span>
              <p className="font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Tracking Results */}
        {trackingData && (
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Order ID</p>
                  <p className="font-semibold text-gray-900">{trackingData.order._id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tracking ID</p>
                  <p className="font-semibold text-gray-900">{trackingData.order.delhivery?.waybill || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Current Status</p>
                  <p className="font-semibold text-gray-900 capitalize">
                    {getStageIcon(trackingData.order.orderStage)} {getStageLabel(trackingData.order.orderStage)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Order Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(trackingData.order.createdAt)}</p>
                </div>
                {trackingData.order.deliveredAt && (
                  <div>
                    <p className="text-sm text-gray-600">Delivered On</p>
                    <p className="font-semibold text-green-600">{formatDate(trackingData.order.deliveredAt)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Shipment Status */}
            {trackingData.order.delhivery?.shipmentStatus && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Current Shipment Status</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-900 font-medium text-lg">
                    {trackingData.order.delhivery.shipmentStatus}
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    Last Updated: {formatDate(trackingData.order.delhivery.lastUpdated)}
                  </p>
                </div>
              </div>
            )}

            {/* Delivery Progress Stages */}
            {trackingData.order.delhivery?.waybill && (() => {
              const deliveryProgress = getDeliveryProgress(trackingData.order);
              return (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Delivery Progress</h2>
                  
                  {/* Current Status Badge */}
                  <div className="mb-8 text-center">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full">
                      <span className="text-2xl">{deliveryProgress.stages.find(s => s.name === deliveryProgress.currentStage)?.icon}</span>
                      <span className="font-semibold text-blue-900 text-lg">Current: {deliveryProgress.currentStage}</span>
                    </div>
                  </div>

                  {/* Progress Bar - Desktop */}
                  <div className="hidden md:block">
                    <div className="relative">
                      {/* Progress Line */}
                      <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-500"
                          style={{ 
                            width: `${(deliveryProgress.stages.filter(s => s.status === 'completed').length - 1) / (deliveryProgress.stages.length - 1) * 100}%` 
                          }}
                        ></div>
                      </div>
                      
                      {/* Stage Markers */}
                      <div className="relative flex justify-between">
                        {deliveryProgress.stages.map((stage, index) => (
                          <div key={index} className="flex flex-col items-center" style={{ width: '20%' }}>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl mb-3 transition-all ${
                              stage.status === 'completed' ? 'bg-green-500 text-white shadow-lg scale-110' :
                              stage.status === 'current' ? 'bg-blue-500 text-white animate-pulse shadow-lg' :
                              'bg-gray-200 text-gray-400'
                            }`}>
                              {stage.icon}
                            </div>
                            <span className={`text-sm font-medium text-center ${
                              stage.status === 'completed' ? 'text-green-700' :
                              stage.status === 'current' ? 'text-blue-700' :
                              'text-gray-500'
                            }`}>
                              {stage.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar - Mobile */}
                  <div className="md:hidden space-y-4">
                    {deliveryProgress.stages.map((stage, index) => (
                      <div key={index}>
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl flex-shrink-0 ${
                            stage.status === 'completed' ? 'bg-green-500 text-white' :
                            stage.status === 'current' ? 'bg-blue-500 text-white animate-pulse' :
                            'bg-gray-200 text-gray-400'
                          }`}>
                            {stage.icon}
                          </div>
                          <div className="flex-1">
                            <span className={`font-medium ${
                              stage.status === 'completed' ? 'text-green-700' :
                              stage.status === 'current' ? 'text-blue-700' :
                              'text-gray-500'
                            }`}>
                              {stage.name}
                            </span>
                            {stage.status === 'current' && (
                              <p className="text-xs text-blue-600 mt-1">In Progress</p>
                            )}
                            {stage.status === 'completed' && (
                              <p className="text-xs text-green-600 mt-1">✓ Completed</p>
                            )}
                          </div>
                        </div>
                        {index < deliveryProgress.stages.length - 1 && (
                          <div className={`w-0.5 h-8 ml-6 mt-2 ${
                            stage.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                          }`}></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Detailed Tracking Timeline */}
            {trackingData.order.delhivery?.scans && trackingData.order.delhivery.scans.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Detailed Tracking History</h2>
                <div className="space-y-4">
                  {[...trackingData.order.delhivery.scans].reverse().map((scan, index) => {
                    const category = categorizeScan(scan.ScanDetail);
                    const categoryColor = getCategoryColor(category);

                    return (
                      <div
                        key={index}
                        className="flex gap-4 items-start pb-4 border-b border-gray-200 last:border-b-0"
                      >
                        {/* Timeline dot */}
                        <div className="flex-shrink-0 mt-1">
                          <div className={`w-3 h-3 rounded-full ${
                            index === 0 ? 'bg-blue-600' : 'bg-gray-400'
                          }`}></div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${categoryColor}`}>
                              {category}
                            </span>
                            {index === 0 && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Latest
                              </span>
                            )}
                          </div>
                          <p className="font-medium text-gray-900 mb-1">{scan.ScanDetail}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span>📍 {scan.ScannedLocation || 'N/A'}</span>
                            <span>🕒 {formatDate(scan.ScanDateTime)}</span>
                          </div>
                          {scan.Instructions && (
                            <p className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                              ℹ️ {scan.Instructions}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shipping Address */}
            {trackingData.order.shippingAddress && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Delivery Address</h2>
                <div className="text-gray-700">
                  <p className="font-medium">{trackingData.order.shippingAddress.fullName}</p>
                  <p>{trackingData.order.shippingAddress.address}</p>
                  <p>
                    {trackingData.order.shippingAddress.city}, {trackingData.order.shippingAddress.state} - {trackingData.order.shippingAddress.pincode}
                  </p>
                  <p className="mt-2">📞 {trackingData.order.shippingAddress.phone}</p>
                </div>
              </div>
            )}

            {/* Order Items */}
            {trackingData.order.items && trackingData.order.items.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Order Items</h2>
                <div className="space-y-4">
                  {trackingData.order.items.map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <div className="text-sm text-gray-600 mt-1">
                          <p>Size: {item.size || 'N/A'}</p>
                          <p>Quantity: {item.quantity}</p>
                          <p className="font-semibold text-gray-900 mt-1">₹{item.price}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total Amount</span>
                    <span className="text-2xl font-bold text-gray-900">₹{trackingData.order.totalPrice}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicTracking;
